/*
 * Copyright (c) 2025 AirAsia Berhad (Malaysia). All rights reserved.
 *
 * This software is proprietary and confidential. Unauthorized copying of this file,
 * via any medium, is strictly prohibited. Do not distribute, copy, modify, or reuse
 * any part of this code without explicit written permission from AirAsia Move.
 *
 * For internal use only.
 */

import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Purpose } from '../../../managers/sso';
import {
  describeSsoError,
  type ResolvedSsoError,
} from '../../../utils/ssoErrors';
import { useCultureCode } from '../../useCultureCode';
import type { OtpFlow } from '../types';
import { useRequestEmailOtp, useVerifyEmailOtp } from '../api';

export interface UseEmailOtpPurposeFlowOptions {
  email?: string;
  otpPurpose: Purpose;
  startTimer: () => void;
  onVerified?: (data?: { emailOtpAuthToken?: string }) => void | Promise<void>;
}

export function useEmailOtpPurposeFlow({
  email,
  otpPurpose,
  startTimer,
  onVerified,
}: UseEmailOtpPurposeFlowOptions): OtpFlow {
  const { t } = useTranslation('account');
  const cultureCode = useCultureCode('useEmailOtpPurposeFlow');

  const [error, setError] = useState<ResolvedSsoError>();

  const setRequestError = useCallback(
    (rawError: unknown, fallback: string) =>
      setError(describeSsoError(rawError, t, fallback)),
    [t],
  );

  const requestEmailOtpMutation = useRequestEmailOtp('useEmailOtpPurposeFlow');
  const verifyEmailOtpMutation = useVerifyEmailOtp('useEmailOtpPurposeFlow');

  const hasEmail = useCallback(() => {
    if (!email) {
      setError({ message: t('verifyEmailPage.missingEmail') });
      return false;
    }
    setError(undefined);
    return true;
  }, [email, t]);

  const submitOtp = useCallback(
    (otp: string) => {
      if (!hasEmail()) return;

      verifyEmailOtpMutation.mutate(
        { email: email as string, otp, purpose: otpPurpose },
        {
          onSuccess: (data) => {
            void onVerified?.(data);
          },
          onError: (rawError: unknown) =>
            setRequestError(rawError, t('verifyEmailPage.verificationFailed')),
        },
      );
    },
    [
      email,
      hasEmail,
      onVerified,
      setRequestError,
      t,
      verifyEmailOtpMutation,
      otpPurpose,
    ],
  );

  const requestOtp = useCallback(() => {
    if (!hasEmail()) return;

    requestEmailOtpMutation.mutate(
      {
        email: email as string,
        cultureCode,
        purpose: otpPurpose,
      },
      {
        onSuccess: () => startTimer(),
        onError: (rawError: unknown) => {
          setRequestError(rawError, t('verifyEmailPage.requestFailed'));
          startTimer();
        },
      },
    );
  }, [
    cultureCode,
    email,
    hasEmail,
    requestEmailOtpMutation,
    setRequestError,
    startTimer,
    t,
    otpPurpose,
  ]);

  const clearError = useCallback(() => {
    setError(undefined);
    requestEmailOtpMutation.reset();
    verifyEmailOtpMutation.reset();
  }, [requestEmailOtpMutation, verifyEmailOtpMutation]);

  return {
    submitOtp,
    requestOtp,
    errorMessage: error?.message,
    errorCode: error?.code,
    isLoading:
      requestEmailOtpMutation.isPending || verifyEmailOtpMutation.isPending,
    clearError,
  };
}
